import type { AgendaReminder, EventReminder, MemberIds, ReminderInput } from '@amumin/api-client';
import type { AppMessages } from '@amumin/i18n';

import { checklistFromItems, checklistInput, type ChecklistDraft } from './checklist.js';

type AppRemindersMessages = AppMessages['reminders'];

// Herinneringen in de afspraak-editor (0021): conceptvorm in het formulier, omzetten van/naar de
// API, vergelijken (alleen gewijzigd meesturen), korte tijdsnotatie ("30 min", "1 uur") en slimme
// voorstellen op basis van de afspraak ("Voetbaltraining" → "Klaarmaken voor trainen").

export const REMINDER_MAX = 5;
export const REMINDER_TITLE_MAX = 60;
export const REMINDER_DEFAULT_ICON = 'get-dressed';
/** De snelle keuzes onder "Wanneer" (minuten vooraf). */
export const REMINDER_OFFSETS = [10, 15, 30, 45, 60] as const;

export interface ReminderDraft {
  /** Bestaande herinnering (bijwerken); ontbreekt = nieuw. */
  id?: string;
  /** Lokale sleutel voor de lijst (ook voor nieuwe). */
  key: string;
  offset_minutes: number;
  title: string;
  icon: string;
  /** Leeg = alle leden van de afspraak. */
  member_ids: string[];
  speak: boolean;
  sound: boolean;
  /** 0031: wat er mee moet (naast de oproep op de tablet). */
  checklist?: ChecklistDraft[];
}

let seq = 0;
export function draftKey(): string {
  seq += 1;
  return `r${Date.now().toString(36)}${seq}`;
}

export function draftsFromDetail(list: ReadonlyArray<EventReminder> | undefined): ReminderDraft[] {
  return (list ?? []).map((r) => ({
    id: r.id,
    key: r.id,
    offset_minutes: r.offset_minutes,
    title: r.title,
    icon: r.icon,
    member_ids: [...r.member_ids],
    speak: r.speak,
    sound: r.sound,
    ...(r.checklist ? { checklist: checklistFromItems(r.checklist) } : {}),
  }));
}

export function remindersInput(list: ReadonlyArray<ReminderDraft>): ReminderInput[] {
  return list.map((r) => ({
    ...(r.id ? { id: r.id } : {}),
    offset_minutes: r.offset_minutes,
    title: r.title.trim(),
    icon: r.icon || REMINDER_DEFAULT_ICON,
    member_ids: [...r.member_ids],
    speak: r.speak,
    sound: r.sound,
    ...(r.checklist ? { checklist: checklistInput(r.checklist) } : {}),
  }));
}

export function sameReminders(
  a: ReadonlyArray<ReminderDraft>,
  b: ReadonlyArray<ReminderDraft>,
): boolean {
  return JSON.stringify(remindersInput(a)) === JSON.stringify(remindersInput(b));
}

/** Leden van een herinnering die (nog) bij de afspraak horen; leeg blijft leeg (= iedereen). */
export function keepEventMembers(ids: ReadonlyArray<string>, event: MemberIds): string[] {
  if (event === 'everyone') return [...ids];
  return ids.filter((id) => event.includes(id));
}

/**
 * Wie de tablet roept (zoals de server, review r1): de gekozen leden, of zonder keuze de kinderen
 * van de afspraak (alle leden als er geen kinderen bij zijn). Kinderen altijd vooraan.
 */
export function reminderPeople<T extends { id: string; kind?: string | undefined }>(
  ids: ReadonlyArray<string>,
  eventMembers: ReadonlyArray<T>,
): T[] {
  const isChild = (m: T): boolean => m.kind === 'child';
  let who: T[];
  if (ids.length > 0) who = eventMembers.filter((m) => ids.includes(m.id));
  else {
    const kids = eventMembers.filter(isChild);
    who = kids.length > 0 ? kids : [...eventMembers];
  }
  return who.filter(isChild).concat(who.filter((m) => !isChild(m)));
}

/** Zonder keuze "Alle kinderen" als de afspraak kinderen én volwassenen heeft, anders "Iedereen". */
export function everyoneMeansKids(
  eventMembers: ReadonlyArray<{ kind?: string | undefined }>,
): boolean {
  const kids = eventMembers.filter((m) => m.kind === 'child').length;
  return kids > 0 && kids < eventMembers.length;
}

export interface PrunedDraft {
  title: string;
  /** Wie er niet meer bij de afspraak hoort. */
  gone: string[];
  removed: boolean;
}

/**
 * Leden die niet meer bij de afspraak horen uit de herinneringen halen (review r1). Een
 * herinnering waarvan ALLE gekozen leden weg zijn, gaat eruit: leeg zou "iedereen" betekenen en
 * dan ineens een ander kind roepen. Geeft per aangepaste herinnering een notitie voor de ouder.
 */
export function pruneDrafts(
  drafts: ReadonlyArray<ReminderDraft>,
  event: MemberIds,
): { drafts: ReminderDraft[]; notes: PrunedDraft[] } {
  const notes: PrunedDraft[] = [];
  const out: ReminderDraft[] = [];
  for (const d of drafts) {
    const kept = keepEventMembers(d.member_ids, event);
    if (kept.length === d.member_ids.length) {
      out.push(d);
      continue;
    }
    const gone = d.member_ids.filter((id) => !kept.includes(id));
    if (kept.length === 0) {
      notes.push({ title: d.title, gone, removed: true });
      continue;
    }
    notes.push({ title: d.title, gone, removed: false });
    out.push({ ...d, member_ids: kept });
  }
  return { drafts: out, notes };
}

function sameMemberIds(a: MemberIds, b: MemberIds): boolean {
  if (a === 'everyone' || b === 'everyone') return a === b;
  return a.length === b.length && a.every((id) => b.includes(id));
}

/**
 * Wat er bij opslaan met de herinneringen gebeurt (review r2). Het formulier bewaart de lijst
 * zoals de ouder hem maakte; bijsnoeien is afgeleid en gebeurt alleen als de ouder de leden van de
 * afspraak in dit formulier wijzigt (t.o.v. `base`, de leden bij het openen). Zo snoeit het openen
 * van een keer waar Yakub "alleen deze keer" niet meedoet niets weg, en komt een herinnering terug
 * als de ouder Yakub weer aanvinkt. `base` null = nieuwe afspraak: altijd tegen de huidige leden.
 */
export function effectiveReminders(
  drafts: ReadonlyArray<ReminderDraft>,
  current: MemberIds,
  base: MemberIds | null,
): { drafts: ReminderDraft[]; notes: PrunedDraft[] } {
  if (base !== null && sameMemberIds(current, base)) return { drafts: [...drafts], notes: [] };
  return pruneDrafts(drafts, current);
}

/** "30 min", "1 uur", "1 uur 30 min". */
export function formatOffset(minutes: number, r: AppRemindersMessages): string {
  if (minutes < 60) return r.minutes.replace('{count}', String(minutes));
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (rest === 0) return r.hours.replace('{count}', String(hours));
  return r.hoursMinutes.replace('{hours}', String(hours)).replace('{minutes}', String(rest));
}

/** Chiptekst "30 min vooraf · Klaarmaken voor trainen". */
export function reminderChipText(
  reminder: Pick<AgendaReminder, 'offset_minutes' | 'title'>,
  r: AppRemindersMessages,
): string {
  return r.chip
    .replace('{when}', formatOffset(reminder.offset_minutes, r))
    .replace('{title}', reminder.title);
}

/** "HH:MM" min `offset` minuten (wandklok, over middernacht heen). */
export function fireTime(startHhmm: string, offset: number): string {
  const m = /^(\d{2}):(\d{2})$/.exec(startHhmm);
  if (!m) return '';
  const total = (((Number(m[1]) * 60 + Number(m[2]) - offset) % 1440) + 1440) % 1440;
  const h = Math.floor(total / 60);
  const mm = total % 60;
  return `${h < 10 ? '0' : ''}${h}:${mm < 10 ? '0' : ''}${mm}`;
}

export interface ReminderSuggestion {
  title: string;
  icon: string;
}

function norm(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
}

/**
 * Voorstellen voor de tekst op basis van de afspraak: training/voetbal/sport → "Klaarmaken voor
 * trainen" (omkleden), zwemmen → "Zwemtas pakken", moskee/school/les → "Tas klaarzetten", en
 * altijd "Klaarmaken voor {afspraak}" en "Schoenen en jas aan". Het eerste voorstel is de
 * standaardtekst van een nieuwe herinnering.
 */
export function suggestReminders(
  event: { title: string; icon: string | null; category: string | null },
  r: AppRemindersMessages,
): ReminderSuggestion[] {
  const text = norm(event.title);
  const icon = event.icon ?? '';
  const out: ReminderSuggestion[] = [];
  const add = (title: string, pic: string) => {
    if (!out.some((x) => x.title === title)) out.push({ title, icon: pic });
  };
  const swim = /zwem/.test(text) || icon === 'swimming' || icon === 'swim';
  const training =
    /(voetbal|training|trainen|hockey|judo|turnen|gym|dans|tennis|basketbal|korfbal|sport)/.test(
      text,
    ) ||
    ['soccer', 'sport', 'hockey', 'judo', 'gymnastics', 'dance', 'tennis', 'basketball'].includes(
      icon,
    );
  const bag =
    /(moskee|koran|school|les\b|lessen|bijles|muziekles|pianoles)/.test(text) ||
    event.category === 'school' ||
    icon === 'school' ||
    icon === 'backpack';
  if (swim) add(r.suggestSwim, 'backpack');
  if (training && !swim) add(r.suggestTraining, REMINDER_DEFAULT_ICON);
  if (bag && !swim) add(r.suggestBag, 'backpack');
  const t = event.title.trim();
  add(
    t
      ? r.suggestGeneric.replace('{title}', t.charAt(0).toLowerCase() + t.slice(1))
      : r.suggestGenericEmpty,
    REMINDER_DEFAULT_ICON,
  );
  add(r.suggestShoes, 'shoes');
  return out.filter((s) => s.title.length <= REMINDER_TITLE_MAX);
}
